import { Component, Injector, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { addHours, format } from 'date-fns';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { ValueOf } from '@cureskin/api-client/src/common';
import { AppConfig } from '../../../app.config';
import { ConnectionService } from '../../../../services/connection-service';
import { Broadcaster } from '../../../../components/broadcaster';
import { AudioRecorderComponent } from '../../../../components/audio-recorder/audio-recorder.component';

@Component({
    selector: 'pending-call-complete', templateUrl: './pending-call-complete.html',
    standalone: false
})
export class PendingCallCompleteComponent {
  @ViewChild(AudioRecorderComponent) audioRecorderComponent: AudioRecorderComponent;

  private objectId: string;
  public type: string;
  private referenceId: string;
  userNeedForConfirmation: string;
  followUpCallNeeded: boolean = false;
  followUpCallTime: string;
  reason: ValueOf<typeof ApiClientConstant.PendingCall.Status>;
  subReason: string = '';
  note: string = '';
  options: Array<{ display: string, value: string }> = [];
  subOptions: { [key: string]: Array<{ display: string, value: string }> } = {};
  languageOptions: Array<string> = [];
  preferedLanguage: string;
  teamOptions: Array<string> = [];
  team: string;
  referenceMessage: string;
  audioRecordedLink: string;
  audioNote: string;
  data: any = {};
  teenager: boolean;
  userId: string;

  constructor(
    public dialogRef: MatDialogRef<PendingCallCompleteComponent>,
    public injector: Injector,
    public appConfig: AppConfig,
    public broadcasterService: Broadcaster,
    public connectionService: ConnectionService) {
    const data = this.injector.get(MAT_DIALOG_DATA);
    this.data = data;
    this.objectId = data.objectId;
    this.referenceId = data.referenceId;
    this.preferedLanguage = data.languagePreference;
    this.type = data.type;
    this.teenager = data.teenager;
    [this.team] = data?.teams || [''];
    this.userId = data.userId;
    this.addOptions();
    this.languageOptions = Object.values(ApiClientConstant.LanguageString);
    this.teamOptions = AppConfig.Shared.Team.Name;
  }

  private addOptions(): void {
    switch (this.type) {
      case ApiClientConstant.PendingCall.Type.OnlinePaymentPending:
      case ApiClientConstant.PendingCall.Type.DoctorOrder:
      case ApiClientConstant.PendingCall.Type.ReacquisitionCall:
      case ApiClientConstant.PendingCall.Type.InitialOrder: {
        this.options.push(
          { display: 'Canceled', value: 'Canceled' },
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'OrderPlaced', value: 'OrderPlaced' },
          { display: 'Call Done', value: 'CallCompleted' },
          { display: 'Duplicate', value: 'Duplicate' },
          { display: 'OrderNotPlaced', value: 'OrderNotPlaced' },
          { display: 'Online payment pending', value: 'Online payment pending' },
          { display: 'Other', value: 'Other' });
        break;
      }
      case ApiClientConstant.PendingCall.Type.MissedCall: {
        this.options.push(
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Call Done', value: 'CallCompleted' },
          { display: 'Other', value: 'Other' });
        break;
      }
      case ApiClientConstant.PendingCall.Type.WaitingForImage: {
        this.options.push(
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Picked and Disconnected', value: 'PickedAndDisconnected' },
          { display: 'Cancel Order', value: 'CancelOrder' },
          { display: 'User will upload', value: 'UserWillUpload' },
          { display: 'Not Serviceable', value: 'NotServiceable' },
          { display: 'Image Verified', value: 'ImageVerified' },
          { display: 'Other', value: 'Other' });
        break;
      }
      case this.appConfig.Shared.PendingCall.Type.UnpaidUserDoctorCall: {
        this.options.push(
          { display: 'Call Done', value: 'CallCompleted' },
          { display: 'Call Unsuccessful', value: 'NotReachable' },
          { display: 'Call Later', value: 'CallLater' });
        break;
      }
      case ApiClientConstant.PendingCall.Type.AddressConfirmation: {
        this.options.push(
          { display: 'Address Confirmed', value: 'AddressConfirmed' },
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Picked and Disconnected', value: 'PickedAndDisconnected' },
          { display: 'Duplicate', value: 'Duplicate' },
          { display: 'Call Done', value: 'CallCompleted' },
          { display: 'Call Later', value: 'CallLater' },
          { display: 'Other', value: 'Other' });
        break;
      }
      case ApiClientConstant.PendingCall.Type.TeenagerConsentConfirmation: {
        this.options.push(
          { display: 'Consent Confirmed', value: 'ConsentConfirmed' },
          { display: 'Consent Not Confirmed', value: 'ConsentNotConfirmed' },
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Picked and Disconnected', value: 'PickedAndDisconnected' },
          { display: 'Retry Call Again', value: 'FollowUpCall' },
          { display: 'Other', value: 'Other' });
        break;
      }
      case ApiClientConstant.PendingCall.Type.IsotretinoinConsentCall: {
        this.options.push(
          { display: 'Isotretinoin Consent Confirmed', value: 'IsotretinoinConsentConfirmed' },
          { display: 'Isotretinoin Consent Not Confirmed', value: 'IsotretinoinConsentDenied' },
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Picked and Disconnected', value: 'PickedAndDisconnected' },
          { display: 'Retry Call Again', value: 'FollowUpCall' },
          { display: 'Other', value: 'Other' });
        break;
      }
      default: {
        this.options.push(
          { display: 'Not Reachable', value: 'NotReachable' },
          { display: 'Ringing or No Response', value: 'RingingOrNoResponse' },
          { display: 'Picked and Disconnected', value: 'PickedAndDisconnected' },
          { display: 'Duplicate', value: 'Duplicate' },
          { display: 'Call Done', value: 'CallCompleted' },
          { display: 'Call Later', value: 'CallLater' },
          { display: 'Other', value: 'Other' });
      }
    }
    const canceledOrOrderNotPlacedSubStatus = [
      { display: 'Recently ordered same products', value: 'recentlyOrderedSameProducts' },
      { display: 'Will check and order', value: 'WillCheckAndOrder' },
      { display: 'Financial issues', value: 'FinancialIssues' },
      { display: 'Buy Later', value: 'BuyLater' },
      { display: 'Check with Family', value: 'CheckWithFamily' },
      { display: 'Window Shopping', value: 'WindowShopping' },
      { display: 'High Price', value: 'HighPrice' },
      { display: 'Results issue', value: 'ResultsIssue' },
      { display: 'Out of Station', value: 'OutOfStation' },
      { display: 'No Improvements', value: 'NoImprovements' },
    ];
    this.subOptions.Canceled = canceledOrOrderNotPlacedSubStatus;
    this.subOptions.OrderNotPlaced = canceledOrOrderNotPlacedSubStatus;
    this.subOptions.CancelOrder = this.subOptions.Canceled;
    if (this.teenager) {
      this.subOptions.ConsentConfirmed = [
        { display: 'Confirm By Parent', value: 'ConfirmByParent' },
        { display: 'Confirm By Teenager', value: 'ConfirmByTeenager' },
      ];
    }
  }

  getTwoHoursFromNow(): string {
    const now = new Date();
    const twoHoursLater = addHours(now, 2);
    return format(twoHoursLater, 'yyyy-MM-dd\'T\'HH:mm');
  }

  enableFollowUp(): void {
    if (this.reason === 'RingingOrNoResponse' || this.reason === 'CallLater') {
      this.followUpCallNeeded = true;
      this.followUpCallTime = this.getTwoHoursFromNow();
    } else {
      this.followUpCallNeeded = false;
    }
  }

  async saveResponse(): Promise<void> {
    try {
      const pendingCall = new Table.PendingCall();
      pendingCall.id = this.objectId;
      const context: any = {};
      const isRequestedByDoctor = this.connectionService.getCurrentUser()?.get('type') === ApiClientConstant.User.Type.DOCTOR;
      if (isRequestedByDoctor) {
        pendingCall.set('reservedForTeam', ['Doctor']);
      }
      if (this.followUpCallNeeded && this.followUpCallTime) {
        pendingCall.set('followUpCallTime', new Date(this.followUpCallTime));
      }
      if (pendingCall.get('languagePreference') !== this.preferedLanguage) {
        context.languagePreference = this.preferedLanguage;
      }
      if ((pendingCall.get('teams') && pendingCall.get('teams')[0]) !== this.team) {
        context.teams = [this.team];
      }
      if (pendingCall.get('referenceMessage') !== this.referenceMessage && this.referenceMessage) {
        context.referenceMessage = this.referenceMessage;
      }
      if (this.subOptions[this.reason]) {
        pendingCall.set('subStatus', this.subReason);
      }
      if (this.type === ApiClientConstant.PendingCall.Type.IsotretinoinConsentCall
        && this.reason === 'IsotretinoinConsentDenied') {
        await this.connectionService.processIsotretinoinConsent(this.referenceId, false);
      }
      await pendingCall.save({ status: this.reason, note: this.note }, { context });
      this.dialogRef.close(true);
    } catch (error) {
      this.broadcasterService.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  async sendFeedbackForm(): Promise<void> {
    const currentUser = this.connectionService.getCurrentUser();
    try {
      await this.connectionService.createNPSFeedback({
        userId: this.userId,
        createdByUserId: currentUser.id,
      });
      this.broadcasterService.broadcast('NOTIFY', { message: 'Sent Successfully', type: this.appConfig.Shared.Toast.Type.SUCCESS });
    } catch (error) {
      this.broadcasterService.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }
}
